import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiService } from '../../shared/services/api.service';
import { sessionToken } from '../../shared/services/jwt.service';
import { UserService } from '../../shared/services/user.service';
import { Workstation, WorkstationSession, VoipConfig, CallLogsResponse, CallStatsResponse, SipAccount, Recording, CallLogInput } from '../models/voip.models';
import { toQuery } from './voip-workstation-query';

@Injectable({
    providedIn: 'root'
})
export class VoipWorkstationService {

    constructor(
        private apiService: ApiService,
        private userService: UserService
    ) {}

    private isManager(): boolean {
        const currentUser = this.userService.getCurrentUser();
        return currentUser?.role === 'ROLE_API_MANAGER';
    }

    private getBaseUrl(): string {
        return this.isManager() ? '/manager/voip' : '/caregiver/voip';
    }

    // Uniwersalne metody - wykrywają rolę użytkownika
    getAvailableWorkstations(): Observable<any> {
        return this.apiService.get(`${this.getBaseUrl()}/available-workstations`);
    }

    loginToWorkstation(workstationId: number): Observable<any> {
        return this.apiService.post(`${this.getBaseUrl()}/login/${workstationId}`, {});
    }

    logoutFromWorkstation(): Observable<any> {
        return this.apiService.post(`${this.getBaseUrl()}/logout`, {});
    }

    getMySession(): Observable<WorkstationSession> {
        return this.apiService.get(`${this.getBaseUrl()}/my-session`);
    }

    getVoipStatus(): Observable<any> {
        return this.apiService.get(`${this.getBaseUrl()}/status`);
    }

    // Przerwy
    startBreak(): Observable<any> {
        return this.apiService.post(`${this.getBaseUrl()}/break/start`, {});
    }

    endBreak(): Observable<any> {
        return this.apiService.post(`${this.getBaseUrl()}/break/end`, {});
    }

    // Żądanie wysyłane w trakcie 'beforeunload'. Zwykłe żądania Angulara nie zdążą wtedy
    // polecieć, a sendBeacon nie ustawi nagłówka X-Access-Token — dlatego fetch z keepalive,
    // który przeżyje zamknięcie strony. Best-effort: strony już nie ma, więc nie ma komu
    // pokazać błędu ani ponowić próby.
    private postBeacon(path: string, body: unknown): void {
        try {
            fetch(`${environment.api_url}${this.getBaseUrl()}${path}`, {
                method: 'POST',
                keepalive: true,
                headers: {
                    'Content-Type': 'application/json',
                    'X-Access-Token': sessionToken() || ''
                },
                body: JSON.stringify(body)
            }).catch(() => {});
        } catch (e) {}
    }

    // Zapis końca przerwy przy odświeżeniu / zamknięciu karty. NIE wylogowuje ze stanowiska
    // — odświeżenie nie kończy zmiany operatora, sesja zostaje, przerwa dostaje czas końca.
    endBreakBeacon(): void {
        this.postBeacon('/break/end', {});
    }

    // Dla Managera
    getManagerVoipConfig(): Observable<VoipConfig> {
        return this.apiService.get('/manager/voip/config');
    }

    getManagerWorkstations(): Observable<{ data: Workstation[] }> {
        return this.apiService.get('/manager/voip/workstations');
    }

    getWorkstationStatus(): Observable<any> {
        return this.apiService.get('/manager/voip/workstation-status');
    }

    forceLogoutWorkstation(workstationId: number): Observable<any> {
        return this.apiService.post(`/manager/voip/workstations/${workstationId}/force-logout`, {});
    }

    toggleWorkstationActive(workstationId: number): Observable<any> {
        return this.apiService.post(`/manager/voip/workstations/${workstationId}/toggle-active`, {});
    }

    // Samodzielna konfiguracja VoIP przez managera
    saveManagerConfig(config: {
        main_phone_number: string;
        sip_server_login: string;
        sip_server_password: string;
    }): Observable<any> {
        return this.apiService.post('/manager/voip/config', config);
    }

    addWorkstation(workstation: {
        name: string;
        sip_login: string;
        sip_password: string;
        sip_server?: string;
        ws_port?: number;
        uses_desk_phone?: boolean;
    }): Observable<any> {
        return this.apiService.post('/manager/voip/workstations', workstation);
    }

    updateWorkstation(id: number, workstation: {
        name?: string;
        sip_login?: string;
        sip_password?: string;
        uses_desk_phone?: boolean;
    }): Observable<any> {
        return this.apiService.put(`/manager/voip/workstations/${id}`, workstation);
    }

    deleteWorkstation(id: number): Observable<any> {
        return this.apiService.delete(`/manager/voip/workstations/${id}`);
    }

    /**
     * Zleca centrali połączenie z aparatu biurkowego stanowiska, na którym
     * ratownik jest zalogowany. Połączenie nie idzie przez przeglądarkę.
     */
    dialFromDeskPhone(phone: string): Observable<any> {
        return this.apiService.post('/caregiver/voip/dial-desk', { phone });
    }

    /**
     * Rozmowa przychodząca odebrana na aparacie — widget nie widzi jej przez sip.js,
     * więc pyta backend, który zna ją z webhooka centrali.
     */
    getDeskCall(): Observable<any> {
        return this.apiService.get('/caregiver/voip/desk-call');
    }

    /** `{ended: true}`, gdy centrala zgłosiła `callend` dla tej rozmowy na aparacie. */
    getDeskCallOutgoing(since: number): Observable<any> {
        return this.apiService.get(`/caregiver/voip/desk-call?outgoingSince=${since}`);
    }

    transferDeskCall(uniqueId: string): Observable<any> {
        return this.apiService.post('/caregiver/voip/desk-call/transfer', { uniqueId });
    }

    hangupDeskCall(uniqueId: string): Observable<any> {
        return this.apiService.post('/caregiver/voip/desk-call/hangup', { uniqueId });
    }

    getDeskCallStatus(uniqueId: string): Observable<any> {
        return this.apiService.get(`/caregiver/voip/desk-call?uniqueId=${encodeURIComponent(uniqueId)}`);
    }

    /** Agenci CRK i ich stan w centrali (zalogowany / rozmawia) — cele przekazania rozmowy. */
    getTransferTargets(): Observable<any> {
        return this.apiService.get('/caregiver/voip/transfer-targets');
    }

    /** Dziennik przekazań (odpowiedzi centrali na REFER) → var/logs/voip-dial.log na serwerze. */
    logTransfer(entry: any): void {
        this.apiService.post('/caregiver/voip/transfer-log', entry).subscribe(() => {}, () => {});
    }

    /** Zdarzenie przekazania rozmowy → backend dokłada ratownika i seniora i wysyła callback do ERP. */
    reportTransferEvent(entry: any): void {
        this.apiService.post('/caregiver/voip/transfer-event', entry).subscribe(() => {}, () => {});
    }

    // ========== Integracja API SuperVOIP ==========

    saveApiToken(apiToken: string): Observable<any> {
        return this.apiService.post('/manager/voip/api-token', { api_token: apiToken });
    }

    removeApiToken(): Observable<any> {
        return this.apiService.delete('/manager/voip/api-token');
    }

    getCallLogs(filters?: {
        type?: 'incoming' | 'outgoing' | 'all';
        date_from?: string;
        date_to?: string;
        page?: number;
        per_page?: number;
    }): Observable<CallLogsResponse> {
        return this.apiService.get(`/manager/voip/call-logs${toQuery(filters)}`);
    }

    getCallStats(filters?: {
        date_from?: string;
        date_to?: string;
    }): Observable<CallStatsResponse> {
        return this.apiService.get(`/manager/voip/call-stats${toQuery(filters)}`);
    }

    getSipAccounts(): Observable<{ success: boolean; data: SipAccount[] }> {
        return this.apiService.get('/manager/voip/sip-accounts');
    }

    createSipAccount(data: { name: string; description?: string }): Observable<any> {
        return this.apiService.post('/manager/voip/sip-accounts', data);
    }

    getRecordings(filters?: {
        date_from?: string;
        date_to?: string;
    }): Observable<{ success: boolean; data: Recording[] }> {
        return this.apiService.get(`/manager/voip/recordings${toQuery(filters)}`);
    }

    // ========== Logi z lokalnej bazy danych ==========

    getLocalCallLogs(filters?: {
        date_from?: string;
        date_to?: string;
        direction?: string;
        status?: string;
        user_id?: number;
        limit?: number;
        offset?: number;
    }): Observable<any> {
        return this.apiService.get(`/manager/voip/local-call-logs${toQuery(filters)}`);
    }

    getLocalCallStats(filters?: {
        date_from?: string;
        date_to?: string;
    }): Observable<any> {
        return this.apiService.get(`/manager/voip/local-call-stats${toQuery(filters)}`);
    }

    getSessionLogs(filters?: {
        date_from?: string;
        date_to?: string;
    }): Observable<any> {
        return this.apiService.get(`/manager/voip/session-logs${toQuery(filters)}`);
    }

    // ========== Logowanie połączeń ==========

    logCall(data: CallLogInput): Observable<any> {
        const prefix = this.isManager() ? '/manager/voip' : '/caregiver/voip';
        return this.apiService.post(`${prefix}/log-call`, data);
    }

    // 352: rozmowa zapisywana w chwili zamykania strony (odświeżenie, zamknięcie karty,
    // przeładowanie po obsłudze alarmu). Bez tego nie trafiała do historii w ogóle —
    // zwykłe `logCall` zostaje wtedy anulowane razem ze stroną.
    logCallBeacon(data: CallLogInput): void {
        this.postBeacon('/log-call', data);
    }

    // ========== Wyszukiwanie pacjenta po numerze telefonu ==========

    findPatientByPhone(phone: string): Observable<any> {
        return this.apiService.get(`${this.getBaseUrl()}/find-patient-by-phone?phone=${encodeURIComponent(phone)}`);
    }

    createCallAlarm(data: {
        phone: string;
        direction: 'incoming' | 'outgoing';
        patient_id?: number;
        imei?: string;
        existing_alarm_id?: number;
    }): Observable<any> {
        return this.apiService.post(`${this.getBaseUrl()}/create-call-alarm`, data);
    }

    getTeamStatus(): Observable<any> {
        return this.apiService.get(`${this.getBaseUrl()}/team-status`);
    }

    getRecentCalls(limit: number = 10): Observable<any> {
        if (this.isManager()) {
            return this.apiService.get(`/manager/voip/local-call-logs?limit=${limit}`);
        }
        return this.apiService.get(`/caregiver/voip/recent-calls?limit=${limit}`);
    }

    // ========== Transkrypcja nagrań ==========

    transcribeRecording(recordingId: string | number): Observable<any> {
        return this.apiService.post(`${this.getBaseUrl()}/transcribe/${recordingId}`, {});
    }

    // alarmId — rozmowa z okna obsługi alarmu: transkrypcja trafi też do opisu tego alarmu
    autoTranscribe(phoneNumber: string, direction: string, alarmId?: number | string): Observable<any> {
        return this.apiService.post(`${this.getBaseUrl()}/auto-transcribe`, {
            phone_number: phoneNumber,
            direction: direction,
            alarm_id: alarmId || null
        });
    }

    getTranscriptionsByPhone(phoneNumber: string): Observable<any> {
        return this.apiService.get(`${this.getBaseUrl()}/transcriptions/by-phone/${encodeURIComponent(phoneNumber)}`);
    }

    // Rozmowy pacjenta (VoIP + Reach) — tylko manager/opiekun z przywilejem voip / enableAiParamedic
    getTranscriptionsByImei(imei: string, limit = 20, offset = 0): Observable<any> {
        return this.apiService.get(`${this.getBaseUrl()}/transcriptions/by-imei/${imei}?limit=${limit}&offset=${offset}`);
    }

    getTranscriptionsByUserId(userId: number, limit = 20, offset = 0): Observable<any> {
        return this.apiService.get(`${this.getBaseUrl()}/transcriptions/by-userid/${userId}?limit=${limit}&offset=${offset}`);
    }

    getTranscriptionsByAlarm(alarmId: number | string): Observable<any> {
        return this.apiService.get(`${this.getBaseUrl()}/transcriptions/by-alarm/${alarmId}`);
    }

    getTranscription(recordingId: string | number): Observable<any> {
        return this.apiService.get(`${this.getBaseUrl()}/transcription/${recordingId}`);
    }

    getRecordingAudio(recordingId: string): Observable<Blob> {
        return this.apiService.get(`${this.getBaseUrl()}/recording/${recordingId}/audio`, null, { responseType: 'blob' });
    }

    regenerateAiSummary(recordingId: string | number): Observable<any> {
        return this.apiService.post(`/manager/voip/transcription/${recordingId}/regenerate-summary`, {});
    }

    listTranscriptions(limit = 50, offset = 0): Observable<any> {
        return this.apiService.get(`/manager/voip/transcriptions?limit=${limit}&offset=${offset}`);
    }

    // ==================== AI Patient Summary ====================

    private getAiBaseUrl(): string {
        return this.isManager() ? '/manager/ai' : '/caregiver/ai';
    }

    getPatientAiSummary(imei: string): Observable<any> {
        const lang = localStorage.getItem('lang') || 'pl';
        return this.apiService.get(`${this.getAiBaseUrl()}/patient-summary/${imei}?lang=${lang}`);
    }

    regeneratePatientAiSummary(imei: string): Observable<any> {
        const lang = localStorage.getItem('lang') || 'pl';
        return this.apiService.post(`${this.getAiBaseUrl()}/patient-summary/${imei}/regenerate?lang=${lang}`, {});
    }

    queryPatientAi(imei: string, query: string): Observable<any> {
        const lang = localStorage.getItem('lang') || 'pl';
        return this.apiService.post(`${this.getAiBaseUrl()}/patient-query/${imei}?lang=${lang}`, { query });
    }

    // ========== Numery wewnętrzne ratowników ==========

    /**
     * Lista numerów wewnętrznych ratowników. `withStatus` dokłada do każdego wiersza
     * stan rejestracji w centrali — jednym zapytaniem zamiast osobnego na wiersz.
     * Bez tego parametru endpoint w ogóle nie dotyka SuperVOIP, więc działa też,
     * gdy centrala leży.
     */
    getParamedicSipAccounts(withStatus: boolean = false): Observable<any> {
        return this.apiService.get(
            `/manager/voip/paramedic-sip-accounts${withStatus ? '?with_status=1' : ''}`
        );
    }

    getAvailableSipAccounts(): Observable<any> {
        return this.apiService.get('/manager/voip/available-sip-accounts');
    }

    getParamedicsWithoutSip(): Observable<any> {
        return this.apiService.get('/manager/voip/paramedics-without-sip');
    }

    assignSipToParamedic(data: { user_id: number; sip_number: string; sip_password?: string }): Observable<any> {
        return this.apiService.post('/manager/voip/paramedic-sip-accounts', data);
    }

    updateParamedicSip(
        id: number,
        data: { internal_number?: string; is_active?: boolean; sip_password?: string }
    ): Observable<any> {
        return this.apiService.put(`/manager/voip/paramedic-sip-accounts/${id}`, data);
    }

    /**
     * Stan rejestracji jednego konta w centrali — z listą aktywnych rejestracji
     * (czym i skąd ratownik jest zalogowany). Zbiorczo tego nie ma: SuperVOIP oddaje
     * `loginStatuses` tylko w odpowiedzi o pojedyncze konto.
     */
    getParamedicSipStatus(id: number): Observable<any> {
        return this.apiService.get(`/manager/voip/paramedic-sip-accounts/${id}/status`);
    }

    deleteParamedicSip(id: number): Observable<any> {
        return this.apiService.delete(`/manager/voip/paramedic-sip-accounts/${id}`);
    }

    getSessionStats(filters?: {
        date_from?: string;
        date_to?: string;
    }): Observable<any> {
        return this.apiService.get(`/manager/voip/session-stats${toQuery(filters)}`);
    }
}
